import { HttpErrorResponse } from '@angular/common/http';

/**
 * Error classification that understands both the Apollo Client 3 and 4 error
 * shapes. Everything is duck-typed so this file imports nothing from
 * `@apollo/client`.
 */

export interface GraphQLErrorLike {
  message: string;
  extensions?: Record<string, unknown>;
}

export class NetworkLostError extends Error {
  override name = 'NetworkLostError';
  readonly status = 0;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;

/** AC3 `ApolloError.networkError` when present, otherwise the input itself (AC4 delivers the link error directly). */
export function networkErrorOf(error: unknown): unknown {
  return asRecord(error)?.['networkError'] ?? error;
}

export function isNetworkLost(error: unknown): boolean {
  return error instanceof NetworkLostError || (error instanceof HttpErrorResponse && error.status === 0);
}

/** `HttpErrorResponse.status`, or the numeric `statusCode` of an AC4 `ServerError`. */
export function httpStatusOf(error: unknown): number | undefined {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }
  const statusCode = asRecord(error)?.['statusCode'];
  return typeof statusCode === 'number' ? statusCode : undefined;
}

const messagesOf = (body: unknown): string[] => {
  const errors = asRecord(body)?.['errors'];
  if (!Array.isArray(errors)) {
    return [];
  }
  return errors
    .map((e) => asRecord(e)?.['message'])
    .filter((m): m is string => typeof m === 'string');
};

/** Messages from a GraphQL error body behind a non-2xx status, in either error shape. */
export function serverErrorMessages(error: unknown): string[] {
  if (error instanceof HttpErrorResponse) {
    return messagesOf(error.error);
  }
  const bodyText = asRecord(error)?.['bodyText'];
  if (typeof bodyText === 'string') {
    try {
      return messagesOf(JSON.parse(bodyText));
    } catch {
      return [];
    }
  }
  return [];
}

/** `graphQLErrors` (AC3 `ApolloError`) or `errors` (AC4 `CombinedGraphQLErrors`). */
export function graphQLErrorsOf(error: unknown): ReadonlyArray<GraphQLErrorLike> {
  const record = asRecord(error);
  const list = record?.['graphQLErrors'] ?? record?.['errors'];
  return Array.isArray(list) ? (list as GraphQLErrorLike[]) : [];
}

export function graphQLErrorCode(error: unknown): string | undefined {
  const code = graphQLErrorsOf(error)[0]?.extensions?.['code'];
  return typeof code === 'string' ? code : undefined;
}

/** True when ANY GraphQL error carries `code`, not just the first. */
export function hasGraphQLErrorCode(error: unknown, code: string): boolean {
  return graphQLErrorsOf(error).some((e) => e.extensions?.['code'] === code);
}
